/**
 * Hook para Controle de Bloqueio de Funcionalidades
 * 
 * DESCRIÇÃO:
 * Hook que determina quais funcionalidades devem ser bloqueadas
 * baseado no status da assinatura, plano atual e problemas de pagamento.
 * Implementa diferentes níveis de bloqueio para manter a experiência
 * do usuário durante problemas temporários.
 * 
 * NÍVEIS DE BLOQUEIO:
 * - NONE: Acesso total
 * - LIMITED: Recursos premium bloqueados, básicos liberados
 * - RESTRICTED: Apenas funcionalidades essenciais
 * - BLOCKED: Acesso mínimo, apenas visualização
 * 
 * CONECTA COM:
 * - useSubscriptionStatus
 * - usePlanPermissions
 * - Sistema de features por plano
 */

import { useMemo } from 'react';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { usePlanPermissions } from './usePlanPermissions';

export type BlockingLevel = 'none' | 'limited' | 'restricted' | 'blocked';

export interface FeatureAccess {
  // Funcionalidades principais
  canUseChat: boolean;
  canUseAI: boolean;
  canViewLeads: boolean;
  canCreateLeads: boolean;
  canEditLeads: boolean;
  canDeleteLeads: boolean;

  // Relatórios e analytics
  canGenerateReports: boolean;
  canViewDashboard: boolean;
  canExportData: boolean;

  // Configurações
  canModifySettings: boolean;
  canConfigureAI: boolean;
  canManageUsers: boolean;

  // Integrações
  canUseWhatsApp: boolean;
  canUseInstagram: boolean;
  canScheduleMessages: boolean;

  // Funcionalidades avançadas
  canUseFollowUp: boolean;
  canUseKanban: boolean;
  canManageAgendamentos: boolean;

  // Novas funcionalidades com restrições de plano
  canImportContacts: boolean;
  canUseMedicalRecord: boolean;
  canUseAIScheduling: boolean;

  // Limites
  maxLeadsPerMonth: number;
  maxMessagesPerMonth: number;
  maxReportsPerMonth: number;

  // Metadados
  blockingLevel: BlockingLevel;
  blockingReason?: string;
  gracePeriodMessage?: string;
  subscriptionTier: string;
}

export const useFeatureBlocking = () => {
  // Chamar hooks diretamente - sem try/catch para manter as regras de React Hooks
  const { status: subscriptionStatus } = useSubscriptionStatus();
  const planPermissions = usePlanPermissions();

  const featureAccess: FeatureAccess = useMemo(() => {
    // Se ainda carregando permissões, mostrar loading
    if (planPermissions.isLoading) {
      return getDefaultAccess('none', 'Carregando...');
    }

    const { isAdmin, planName: permissionPlanName, isPMKPlan } = planPermissions;

    // Admin sempre tem acesso total
    if (isAdmin) {
      return getFullAccess();
    }

    // Se não tem subscriptionStatus mas temos planName do permissions, usar permissions
    // Isso é comum para planos manuais como PMK que não passam pelo Stripe
    if (!subscriptionStatus && permissionPlanName) {
      return getAccessByBlockingLevel('none', planPermissions, undefined, undefined);
    }

    if (!subscriptionStatus) {
      return getDefaultAccess('none', 'Carregando...');
    }

    const {
      isBlocked,
      isInGracePeriod,
      hasPaymentIssues,
      needsPayment,
      urgencyLevel,
      planName,
      daysUntilBlocked
    } = subscriptionStatus;

    // Determinar nível de bloqueio
    let blockingLevel: BlockingLevel = 'none';
    let blockingReason: string | undefined;
    let gracePeriodMessage: string | undefined;

    // Contas sem plano pago não recebem acesso gratuito; trials antigos também ficam bloqueados.
    if (planName === 'free' || planName === 'trial') {
      blockingLevel = 'blocked';
      blockingReason = 'Assinatura necessária - escolha um plano para continuar';
    } else if (isBlocked) {
      blockingLevel = 'blocked';
      blockingReason = 'Assinatura cancelada ou expirada';
    } else if (isInGracePeriod) {
      blockingLevel = 'restricted';
      blockingReason = 'Período de graça - pagamento pendente';
      gracePeriodMessage = daysUntilBlocked
        ? `${daysUntilBlocked} dias restantes antes do bloqueio total`
        : 'Últimos dias antes do bloqueio total';
    } else if (hasPaymentIssues && urgencyLevel === 'high') {
      blockingLevel = 'limited';
      blockingReason = 'Problemas de pagamento detectados';
    } else if (needsPayment && urgencyLevel === 'medium') {
      blockingLevel = 'limited';
      blockingReason = 'Renovação necessária em breve';
    }

    // Aplicar bloqueios baseado no nível
    return getAccessByBlockingLevel(
      blockingLevel,
      planPermissions,
      blockingReason,
      gracePeriodMessage
    );

  }, [subscriptionStatus, planPermissions]);

  return {
    ...featureAccess,
    // isLoading agora é baseado apenas em planPermissions, pois subscriptionStatus sempre retorna um objeto válido
    isLoading: planPermissions.isLoading,
    subscriptionStatus,
    canUpgrade: featureAccess.blockingLevel !== 'none'
  };
};

// Função para obter acesso total (admin)
const getFullAccess = (): FeatureAccess => ({
  // Funcionalidades principais
  canUseChat: true,
  canUseAI: true,
  canViewLeads: true,
  canCreateLeads: true,
  canEditLeads: true,
  canDeleteLeads: true,

  // Relatórios e analytics
  canGenerateReports: true,
  canViewDashboard: true,
  canExportData: true,

  // Configurações
  canModifySettings: true,
  canConfigureAI: true,
  canManageUsers: true,

  // Integrações
  canUseWhatsApp: true,
  canUseInstagram: true,
  canScheduleMessages: true,

  // Funcionalidades avançadas
  canUseFollowUp: true,
  canUseKanban: true,
  canManageAgendamentos: true,

  // Novas funcionalidades
  canImportContacts: true,
  canUseMedicalRecord: true,
  canUseAIScheduling: true,

  // Limites
  maxLeadsPerMonth: 999999,
  maxMessagesPerMonth: 999999,
  maxReportsPerMonth: 999999,

  // Metadados
  blockingLevel: 'none',
  subscriptionTier: 'admin',
});

// Função para obter acesso padrão
const getDefaultAccess = (level: BlockingLevel, reason?: string): FeatureAccess => ({
  // Funcionalidades principais
  canUseChat: true,
  canUseAI: false,
  canViewLeads: true,
  canCreateLeads: true,
  canEditLeads: true,
  canDeleteLeads: false,

  // Relatórios e analytics
  canGenerateReports: false,
  canViewDashboard: true,
  canExportData: false,

  // Configurações
  canModifySettings: false,
  canConfigureAI: false,
  canManageUsers: false,

  // Integrações
  canUseWhatsApp: true,
  canUseInstagram: false,
  canScheduleMessages: false,

  // Funcionalidades avançadas
  canUseFollowUp: false,
  canUseKanban: true,
  canManageAgendamentos: true,

  // Novas funcionalidades (plano básico padrão)
  canImportContacts: false,
  canUseMedicalRecord: false,
  canUseAIScheduling: false,

  // Limites
  maxLeadsPerMonth: 100,
  maxMessagesPerMonth: 1000,
  maxReportsPerMonth: 0, // Básico não tem relatórios IA

  // Metadados
  blockingLevel: level,
  blockingReason: reason,
  subscriptionTier: 'free',
});

// Função para obter acesso baseado no nível de bloqueio
const getAccessByBlockingLevel = (
  level: BlockingLevel,
  planPermissions: any,
  reason?: string,
  gracePeriodMessage?: string
): FeatureAccess => {
  const baseAccess = getDefaultAccess(level, reason);
  baseAccess.gracePeriodMessage = gracePeriodMessage;

  switch (level) {
    case 'none':
      // Acesso baseado no plano específico
      const tier = planPermissions.planName || 'free';
      return getPlanBasedAccess(baseAccess, tier, planPermissions);

    case 'limited':
      // Recursos premium bloqueados, básicos liberados
      return {
        ...baseAccess,
        canUseAI: false, // Bloquear IA durante problemas
        canGenerateReports: false, // Bloquear relatórios
        canConfigureAI: false,
        canModifySettings: true, // Permitir configurações básicas
        canExportData: false,
        canImportContacts: false,
        canUseInstagram: false,
        canScheduleMessages: false,
        canUseFollowUp: false,
        canUseMedicalRecord: false,
        maxLeadsPerMonth: 500, // Limite reduzido
        maxMessagesPerMonth: 2000,
        maxReportsPerMonth: 0,
        blockingLevel: 'limited',
        subscriptionTier: tier
      };

    case 'restricted':
      // Apenas funcionalidades essenciais (período de graça)
      return {
        ...baseAccess,
        canCreateLeads: true, // Manter criação de leads
        canEditLeads: false, // Bloquear edição
        canDeleteLeads: false,
        canModifySettings: false,
        canExportData: false,
        canImportContacts: false,
        canUseInstagram: false,
        canScheduleMessages: false,
        canUseFollowUp: false,
        canUseMedicalRecord: false,
        maxLeadsPerMonth: 100,
        maxMessagesPerMonth: 500,
        maxReportsPerMonth: 0,
        blockingLevel: 'restricted',
        subscriptionTier: tier
      };

    case 'blocked':
      // Acesso mínimo - apenas visualização
      return {
        ...baseAccess,
        canCreateLeads: false,
        canEditLeads: false,
        canDeleteLeads: false,
        canModifySettings: false,
        canManageUsers: false,
        canUseWhatsApp: false,
        canManageAgendamentos: false,
        canImportContacts: false,
        canUseInstagram: false,
        canScheduleMessages: false,
        canUseMedicalRecord: false,
        maxLeadsPerMonth: 0,
        maxMessagesPerMonth: 0,
        maxReportsPerMonth: 0,
        blockingLevel: 'blocked',
        subscriptionTier: tier
      };

    default:
      return { ...baseAccess, subscriptionTier: tier };
  }
};

// Função para determinar acesso baseado no plano específico
const getPlanBasedAccess = (baseAccess: FeatureAccess, tier: string, planPermissions: any): FeatureAccess => {
  switch (tier.toLowerCase()) {
    case 'trial':
    case 'free':
      // Defesa adicional: mesmo fora do gate de rotas, conta não paga recebe acesso zero.
      return getAccessByBlockingLevel(
        'blocked',
        planPermissions,
        'Assinatura necessária - escolha um plano para continuar'
      );

    case 'basic':
    case 'básico':
      return {
        ...baseAccess,
        // Básico: WhatsApp, Meta Ads, Links, Kanban, Agenda
        canUseWhatsApp: true,
        canUseKanban: true,
        canManageAgendamentos: true,
        canViewDashboard: true,
        canModifySettings: true,
        canManageUsers: true,
        canDeleteLeads: true,
        // Restrições do plano básico
        canUseAI: false,
        canGenerateReports: false,
        canExportData: false,
        canImportContacts: false,
        canUseInstagram: false,
        canScheduleMessages: false,
        canUseMedicalRecord: false,
        canUseAIScheduling: false,
        maxReportsPerMonth: 0,
        maxLeadsPerMonth: 99999,
        maxMessagesPerMonth: 99999,
        blockingLevel: 'none',
        subscriptionTier: 'basic'
      };

    case 'pro':
    case 'profissional':
      return {
        ...baseAccess,
        // Tudo do básico +
        canUseWhatsApp: true,
        canUseKanban: true,
        canManageAgendamentos: true,
        canViewDashboard: true,
        canModifySettings: true,
        canManageUsers: true,
        canDeleteLeads: true,
        // Funcionalidades do Pro
        canImportContacts: true,
        canExportData: true,
        canScheduleMessages: true,
        canUseInstagram: true,
        canUseAI: planPermissions.canUseAI,
        canGenerateReports: planPermissions.canGenerateReports,
        canConfigureAI: planPermissions.canConfigureAI,
        maxReportsPerMonth: 2,
        // Restrições do Pro
        canUseMedicalRecord: false,
        canUseAIScheduling: false,
        maxLeadsPerMonth: 99999,
        maxMessagesPerMonth: 99999,
        blockingLevel: 'none',
        subscriptionTier: 'pro'
      };

    case 'pmk':
      return {
        ...baseAccess,
        // PMK: Acesso total (exceto IA se não tiver override)
        canUseWhatsApp: true,
        canUseKanban: true,
        canManageAgendamentos: true,
        canViewDashboard: true,
        canModifySettings: true,
        canManageUsers: true,
        canDeleteLeads: true,
        canImportContacts: true,
        canExportData: true,
        canScheduleMessages: true,
        canUseInstagram: true,
        canUseFollowUp: true,
        canUseMedicalRecord: true,
        canUseAIScheduling: true,
        // IA depende das permissões calculadas no usePlanPermissions
        canUseAI: planPermissions.canUseAI,
        canGenerateReports: planPermissions.canGenerateReports,
        canConfigureAI: planPermissions.canConfigureAI,
        maxReportsPerMonth: 99999,
        maxLeadsPerMonth: 99999,
        maxMessagesPerMonth: 99999,
        blockingLevel: 'none',
        subscriptionTier: 'pmk'
      };

    case 'premium':
      return {
        ...baseAccess,
        // Tudo dos anteriores + Premium features
        canUseWhatsApp: true,
        canUseKanban: true,
        canManageAgendamentos: true,
        canViewDashboard: true,
        canModifySettings: true,
        canManageUsers: true,
        canDeleteLeads: true,
        canImportContacts: true,
        canExportData: true,
        canScheduleMessages: true,
        canUseInstagram: true,
        canUseAI: planPermissions.canUseAI,
        canGenerateReports: planPermissions.canGenerateReports,
        canConfigureAI: planPermissions.canConfigureAI,
        // Recursos exclusivos Premium
        canUseMedicalRecord: true,
        canUseAIScheduling: true,
        canUseFollowUp: true,
        maxReportsPerMonth: 4,
        maxLeadsPerMonth: 99999,
        maxMessagesPerMonth: 99999,
        blockingLevel: 'none',
        subscriptionTier: 'premium'
      };



    default:
      // Plano free ou desconhecido
      return {
        ...baseAccess,
        subscriptionTier: 'free'
      };
  }
};
